import { useEffect, useState } from 'react';
import { ACTIVE_LOCK_WINDOW_MS } from '../constants';
import { useLatestRemoteCommand } from './useLatestRemoteCommand';
import { useRemoteCommandStatusCheck } from './useRemoteCommandStatusCheck';
import { useSendVehicleRemoteCommand } from './useSendVehicleRemoteCommand';

/** ms until an active command stops blocking new ones (0 once the lock window has passed). */
const lockRemainingMs = (requestedAt) => {
	const requested = Date.parse(requestedAt);
	return Number.isNaN(requested) ? 0 : Math.max(0, requested + ACTIVE_LOCK_WINDOW_MS - Date.now());
};

/**
 * Whether the active current command still blocks new commands: true until its
 * lock window (the backend's) passes, then a re-render enables the buttons.
 */
const useBlockedByActive = (latest, isActive) => {
	const requestedAt = latest?.requestedAt;
	const blocked = isActive && lockRemainingMs(requestedAt) > 0;
	const [, setExpired] = useState(0);
	useEffect(() => {
		if (!blocked) return undefined;
		const timer = setTimeout(() => setExpired(Date.now()), lockRemainingMs(requestedAt) + 100);
		return () => clearTimeout(timer);
	}, [blocked, requestedAt]);
	return blocked;
};

/**
 * State of the Remote commands tab: the current command, its status check
 * (`statusCheck`), the command waiting for confirmation (`pending`) and the
 * actions. `request(command)` opens the confirmation; `confirm()` closes it
 * and sends exactly once; buttons are `disabled` while a send is in flight or
 * the current command is active (inside the lock window).
 */
export const useRemoteCommandsTab = (unitId) => {
	const current = useLatestRemoteCommand(unitId);
	const send = useSendVehicleRemoteCommand();
	const statusCheck = useRemoteCommandStatusCheck(current.latest);
	const blocked = useBlockedByActive(current.latest, current.isActive);
	const [pending, setPending] = useState(null);

	const disabled = send.isPending || blocked || !unitId;

	const request = (command) => {
		if (disabled) return;
		setPending(command);
	};

	const cancel = () => setPending(null);

	const confirm = () => {
		if (!pending || send.isPending) return;
		setPending(null);
		send.mutate({ unitId, command: pending });
	};

	return { ...current, statusCheck, disabled, pending, request, cancel, confirm };
};
